"use client";

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Braces, KeyRound, Search, X } from "lucide-react";

import { Button } from "@/components/shadcn/button";
import { Input } from "@/components/shadcn/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/shadcn/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/shadcn/tooltip";
import {
  GroupVariablePublic,
  GroupVariablesService,
} from "@/services/Spotflow/groupVariables";

interface WorkspaceSecretRefPickerProps {
  groupId: string;
  onPick: (variableName: string) => void;
  disabled?: boolean;
}

/** Round icon-only button that opens a searchable workspace variable picker. */
export function WorkspaceSecretRefPicker({
  groupId,
  onPick,
  disabled,
}: WorkspaceSecretRefPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const { data: variables = [] } = useQuery({
    queryKey: ["group-variables", groupId],
    queryFn: () => GroupVariablesService.list(groupId),
    enabled: !!groupId && open,
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return variables;
    return variables.filter((v) => v.name.toLowerCase().includes(q));
  }, [variables, search]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="iconsm"
                className="shrink-0"
                disabled={disabled}
                aria-label="Use a workspace variable or secret"
              >
                <KeyRound
                  className="h-4 w-4 text-muted-foreground"
                  strokeWidth="1.33"
                />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="top">
            Use a workspace variable or secret
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <PopoverContent
        align="end"
        className="w-80 p-0 overflow-hidden"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="border-b border-border px-2 py-1.5 flex items-center gap-1.5">
          <Search className="h-4 w-4 text-muted-foreground shrink-0" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search secrets"
            className="h-7 border-0 px-0 shadow-none focus-visible:ring-0"
            autoFocus
          />
        </div>
        <div className="max-h-72 overflow-y-auto py-1">
          {filtered.length === 0 ? (
            <div className="px-3 py-6 text-xs text-muted-foreground text-center">
              {variables.length === 0
                ? "No workspace secrets yet. Create one in Manage workspace, under Variables."
                : "No matches."}
            </div>
          ) : (
            filtered.map((v) => (
              <Button
                key={v.id}
                type="button"
                variant="ghost"
                className="w-full justify-start gap-2 px-3 py-1.5 text-sm font-normal h-auto"
                onClick={() => {
                  onPick(v.name);
                  setOpen(false);
                  setSearch("");
                }}
              >
                <VariableIcon variable={v} />
                <span className="font-mono truncate flex-1 text-left">
                  {v.name}
                </span>
                <span className="text-xs text-muted-foreground">
                  {v.kind === "secret" ? "Hidden" : "Visible"}
                </span>
              </Button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function VariableIcon({ variable }: { variable: GroupVariablePublic }) {
  const Icon = variable.kind === "secret" ? KeyRound : Braces;
  return (
    <Icon
      className="h-3.5 w-3.5 text-muted-foreground shrink-0"
      strokeWidth={1.5}
    />
  );
}

interface WorkspaceRefChipProps {
  variableName: string;
  onClear: () => void;
  disabled?: boolean;
  /** Workspace id used to resolve the referenced variable's kind so the
   *  chip can render the matching icon (Braces for visible, KeyRound for
   *  hidden). When omitted the chip falls back to KeyRound. */
  groupId?: string;
}

/** Pill rendering of a ``$workspace/<name>`` reference with a remove button. */
export function WorkspaceRefChip({
  variableName,
  onClear,
  disabled,
  groupId,
}: WorkspaceRefChipProps) {
  // Reuses the picker's react-query cache key so the lookup is free if the
  // user has already opened the picker, and only one fetch happens
  // overall for that workspace.
  const { data: variables = [] } = useQuery({
    queryKey: ["group-variables", groupId],
    queryFn: () => GroupVariablesService.list(groupId ?? ""),
    enabled: !!groupId,
  });
  const variable = variables.find((v) => v.name === variableName);
  const Icon = variable?.kind === "variable" ? Braces : KeyRound;

  return (
    <div className="flex items-center gap-2 rounded-md border border-border bg-muted px-2 py-1.5 text-sm">
      <Icon
        className="h-3.5 w-3.5 text-muted-foreground shrink-0"
        strokeWidth={1.5}
      />
      <span className="font-mono truncate flex-1">{variableName}</span>
      {!disabled && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-5 w-5 shrink-0"
          onClick={onClear}
          aria-label="Clear reference"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
}
